import { produce } from "immer";
import React, { useMemo, useState } from "react";
import styled from "styled-components";

import { parseObjectivesItem, parseUnitObjective } from "@examspark/api";
import {
  findUnitObjective,
  reactKeyFromIDSK,
  sortObjectives,
  sortObjectivesItems,
} from "@examspark/common";
import { useQueriesGetPerformanceTasks } from "@examspark/common-app";
import { Button, HSpace, RouterLink } from "@examspark/common-ui";
import { IconCalendarLine } from "@examspark/common-ui/dist/components/icons";
import { colorFromTheme } from "@examspark/common-ui/dist/utils/color";
import {
  AnyResource,
  ParsedTCMPCUnit,
  TCMPCBundle,
  TCMPCRowData,
  ObjectivesItem,
} from "@examspark/types";

import PinResourceToDateModal from "../classroom/units/PinResourceToDateModal";
import ObjectiveBadge from "../common/ObjectiveBadge";
import UnitContent from "../common/UnitContent";
import ObjectiveDetailModal from "../ObjectiveDetailModal";

const Styled = styled.div`
  display: flex;
  flex-direction: column;

  gap: 1rem;

  h3 {
    /* font-weight: normal; */
    font-size: 1.3rem;
    color: #000;
    /* text-decoration: underline; */
  }
`;

const PATitle = styled(HSpace)`
  align-items: center;
  padding: 1rem;
  background-color: ${colorFromTheme("surface2")};

  & > svg {
    fill: #444;
  }
`;

const PA = styled.div`
  border-radius: 0.5rem;
  overflow: hidden;
  border: solid 1px #ddd;
`;
const PABody = styled.div`
  background-color: ${colorFromTheme("surface0")};
  padding: 1rem;
`;
const PAObjectives = styled.div`
  background-color: ${colorFromTheme("surface1")};
  padding: 1rem;
  border-top: solid 1px #ddd;
`;

// const RubricLink = styled.div`
//   padding: 1rem;
//   background-color: ${colorFromTheme("surface1")};
//   border-top: solid 1px #ddd;

//   svg {
//     fill: #444;
//   }
// `;

const Scrollable = styled.div`
  overflow-x: auto;
  contain: inline-size;

  & > table,
  & tr,
  & td,
  & th {
    vertical-align: top;
    padding: 1rem;
    border: none;
    border-collapse: collapse;
    text-align: left;
    font-size: 1.3rem;
  }

  & > table {
    min-width: 50rem;
  }
  & > table > tbody > tr > td {
    border-top: solid 1px #ddd;
  }

  & th {
    border-bottom: solid 1px #ddd;
    vertical-align: bottom;
  }
  & th:first-child,
  & td:first-child {
    padding-left: 0;
  }
`;

type ObjectivesListProps = {
  objectives: ObjectivesItem[] | null | undefined;
  unit: ParsedTCMPCUnit;
};

const ObjectivesList = ({ objectives, unit }: ObjectivesListProps) => {
  const [showModalIndex, setShowModalIndex] = useState<number>();

  const parsedObjectives = useMemo(
    () =>
      sortObjectives(
        objectives
          ?.map((o) => unit.objectives?.find((uo) => uo.id === o.id) ?? o)
          ?.map((o) => parseUnitObjective(o)) ?? [],
      ),
    [objectives, unit.objectives],
  );

  return (
    <>
      <ObjectiveDetailModal
        objectives={parsedObjectives}
        unit={unit}
        showIndex={showModalIndex}
        onShowIndexChange={setShowModalIndex}
      />
      <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem" }}>
        {sortObjectivesItems(objectives ?? []).map((_obj, index) => {
          const obj = parseObjectivesItem(_obj);
          const unitObjective =
            findUnitObjective(unit?.objectives, obj.objective?.id) ?? obj;

          return (
            <div key={reactKeyFromIDSK(obj.objective)}>
              {obj.objective && (
                <ObjectiveBadge
                  showTooltip
                  onClick={() => setShowModalIndex(index)}
                  objectivesItem={unitObjective}
                />
              )}
            </div>
          );
        })}
      </div>
    </>
  );
};

type BundleTableProps = {
  subUnit: TCMPCBundle;
  unit: ParsedTCMPCUnit;
};
const BundleTable = ({ subUnit, unit }: BundleTableProps) => {
  const [showAddToPlannerModal, setShowAddToPlannerModal] =
    React.useState<AnyResource>();

  // first, copy all the spanned data down to the cells its spanning over
  // so that if we slice in the middle of a span when grouping by assesment,
  // we don't lose the data
  const copiedData = produce(subUnit.table.data, (draft) => {
    draft = draft.reverse().reduce((acc, row, idx) => {
      acc[idx] = row;

      const keys = ["understandings", "concepts", "assessments"] as const;
      keys.forEach((key) => {
        for (let i = 1; i < row.columns[key].span && i < acc.length; i++) {
          acc[idx - i].columns[key].data = row.columns[key].data;
          acc[idx - i].columns[key].span = row.columns[key].span - i;
        }
      });

      return acc;
    }, [] as TCMPCRowData[]);
  })
    .slice()
    .reverse();

  // sum up the previous rows' spans to see if you should show yourself
  // annoying bit: if a cell is hidden, don't include it in the sum
  const sumVisibleSpan = React.useCallback(
    (acc: number, curr: number, curIdx: number) =>
      acc + (acc === curIdx ? curr : 0),
    [],
  );

  const shouldHideCell = (data: TCMPCRowData[], idx: number) =>
    Object.entries(
      data.slice(0, idx).reduce(
        (acc, curr, curIdx) => ({
          understandings: sumVisibleSpan(
            acc.understandings,
            curr.columns.understandings.span,
            curIdx,
          ),
          concepts: sumVisibleSpan(
            acc.concepts,
            curr.columns.concepts.span,
            curIdx,
          ),
          assessments: sumVisibleSpan(
            acc.assessments,
            curr.columns.assessments.span,
            curIdx,
          ),
        }),
        { understandings: 0, concepts: 0, assessments: 0 },
      ),
    ).reduce(
      (acc, [key, value]) => ({ ...acc, [key]: value > idx }),
      {} as Record<keyof TCMPCRowData["columns"], boolean>,
    );

  const emptyAssessmentKey = "__EMPTY__";
  const columnsByAssessment = copiedData.reduce(
    (acc, row, idx) => {
      const shouldHide = shouldHideCell(copiedData, idx);
      if (row.columns.assessments.data.length === 0) {
        acc.push({
          idx,
          span: row.columns.assessments.span,
          id: emptyAssessmentKey,
          columns: copiedData.slice(idx, idx + row.columns.assessments.span),
        });
      }
      row.columns.assessments.data.forEach((assessment) => {
        if (!shouldHide.assessments) {
          acc.push({
            idx,
            span: row.columns.assessments.span,
            id: assessment.id,
            columns: copiedData.slice(idx, idx + row.columns.assessments.span),
          });
        }
      });
      return acc;
    },
    [] as { id: string; idx: number; span: number; columns: TCMPCRowData[] }[],
  );

  const uniqueAssessmentIds = Array.from(
    new Set(columnsByAssessment.map((assessment) => assessment.id)),
  );

  const assessmentQueries = useQueriesGetPerformanceTasks(
    uniqueAssessmentIds
      .filter((id) => id !== emptyAssessmentKey)
      .map((id) => ({ id, sk: "resource#v0" })),
  );

  return (
    <>
      <PinResourceToDateModal
        resource={showAddToPlannerModal}
        isOpen={!!showAddToPlannerModal}
        onRequestClose={() => setShowAddToPlannerModal(undefined)}
      />
      <Styled>
        {subUnit.description && <UnitContent content={subUnit.description} />}
        <h3>Overarching Understandings and Questions</h3>
        <UnitContent content={subUnit.ouaqs} />
        {columnsByAssessment.map((assessment, idx) => {
          const assessmentData = assessmentQueries.find(
            (query) => query?.data?.id === assessment.id,
          )?.data;

          return (
            <div
              key={assessment.id === emptyAssessmentKey ? idx : assessment.id}
            >
              <Scrollable>
                <table width="100%">
                  <thead>
                    <tr>
                      <th>Understandings & Questions</th>
                      <th style={{ width: "250px" }}>Concepts</th>
                    </tr>
                  </thead>
                  <tbody>
                    {assessment.columns.map((row, idx) => {
                      const shouldHide = shouldHideCell(
                        assessment.columns,
                        idx,
                      );
                      return (
                        <tr key={idx}>
                          {!shouldHide.understandings && (
                            <td
                              rowSpan={Math.min(
                                row.columns.understandings.span,
                                assessment.columns.length - idx,
                              )}
                            >
                              <UnitContent
                                content={row.columns.understandings.data}
                              />
                            </td>
                          )}
                          {!shouldHide.concepts && (
                            <td
                              rowSpan={Math.min(
                                row.columns.concepts.span,
                                assessment.columns.length - idx,
                              )}
                            >
                              <UnitContent
                                content={row.columns.concepts.data}
                              />
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Scrollable>
              {assessmentData && (
                <PA>
                  <PATitle>
                    <svg
                      version="1.1"
                      id="Layer_1"
                      x="0px"
                      y="0px"
                      viewBox="0 0 512 512"
                      width="24"
                    >
                      <path d="M303.457,472H106.569c-22.056,0-40-17.944-40-40V130c0-22.056,17.944-40,40-40h62.889v10c0,33.084,26.916,60,60,60  s60-26.916,60-60V90h63c22.056,0,40,17.944,40,40v146c0,11.046,8.954,20,20,20s20-8.954,20-20V130c0-44.112-35.888-80-80-80h-63V20  c0-11.046-8.954-20-20-20h-80c-11.046,0-20,8.954-20,20v30h-62.889c-44.112,0-80,35.888-80,80v302c0,44.112,35.888,80,80,80h196.889  c11.046,0,20-8.954,20-20S314.503,472,303.457,472z M209.457,40h40v60c0,11.028-8.972,20-20,20s-20-8.972-20-20V40z M467.9,409.59  L358.34,299.786c-2.327-2.332-5.193-4.054-8.346-5.011l-78.725-23.912c-6.979-2.122-14.56-0.272-19.779,4.821  c-5.221,5.094-7.255,12.626-5.308,19.654l22.362,80.725c0.921,3.322,2.684,6.351,5.119,8.79l109.366,109.573  c11.697,11.696,27.063,17.545,42.427,17.545s30.729-5.849,42.427-17.545C491.278,471.032,491.278,432.968,467.9,409.59z   M294.44,319.705l39.128,11.886l73.924,74.089l-28.289,28.289l-73.513-73.652L294.44,319.705z M439.599,466.142  c-7.797,7.799-20.485,7.799-28.271,0.014l-3.867-3.874l28.284-28.284l3.853,3.862C447.398,445.656,447.398,458.344,439.599,466.142z  " />
                    </svg>
                    <div style={{ flex: "1" }}>
                      <RouterLink to={`/assessment/${assessment.id}`}>
                        {assessmentData.name}
                      </RouterLink>
                    </div>
                    <div>
                      <Button
                        title="Add to Planner"
                        color="create"
                        onClick={() =>
                          setShowAddToPlannerModal({
                            id: assessment.id,
                            sk: "resource#v0",
                          })
                        }
                        icon={<IconCalendarLine />}
                      >
                        Add to Planner
                      </Button>
                    </div>
                  </PATitle>
                  <PABody>
                    <UnitContent content={assessmentData.content?.body ?? ""} />
                  </PABody>
                  <PAObjectives>
                    <ObjectivesList
                      objectives={assessmentData.objectives}
                      unit={unit}
                    />
                  </PAObjectives>
                </PA>
              )}
            </div>
          );
        })}
      </Styled>
    </>
  );
};
export default BundleTable;
